import { useEffect, useState } from 'react'
import { Input, Modal, Radio, Spin, useMessage } from '@pepper/ui'

import { useSelectedCompanyId } from 'utils/functions/misc'
import { useGetKeywordSuggestions } from 'container/SEOTools/hooks/useGetKeywordSuggestions'
import { useGenerateReport } from 'container/SEOTools/hooks/useGenerateReport'

import type { GapReport, TargetKeywordStats } from '@pepper/utils/src/types/seo'

import s from './ChangeKeywordModal.module.css'

type Props = {
  open: boolean
  setOpen: (open: boolean) => void
  report: GapReport
  targetKeywordStats: TargetKeywordStats
}

const ChangeKeywordModal = ({
  open,
  setOpen,
  report,
  targetKeywordStats,
}: Props) => {
  const [inputValue, setInputValue] = useState<string>()
  const [keyword, setKeyword] = useState(inputValue)

  const companyId = useSelectedCompanyId()
  const { showInfo, showError } = useMessage()
  const {
    data: keywords,
    isLoading,
    error,
  } = useGetKeywordSuggestions({
    companyId,
    reportId: report.id,
    enabled: open && !!companyId && !!report.id,
  })
  const {
    mutateAsync: generateReport,
    isLoading: isGeneratingReports,
    isSuccess,
  } = useGenerateReport()

  const handleRadioChange = (option?: string) => {
    setInputValue(undefined)
    setKeyword(option)
  }

  const handleRegenerateReport = async () => {
    await generateReport({
      companyId,
      payload: [
        {
          page_url: report.page_url,
          region_code: report.region_code,
          target_keyword: keyword ?? inputValue,
          existing_report_id: report.id,
        },
      ],
    })
  }

  if (!isLoading && error && open) {
    showError(error.message)
  }

  useEffect(() => {
    let timeout: NodeJS.Timeout
    if (isSuccess) {
      setOpen(false)
      showInfo('Report is being generated, please wait.')
      timeout = setTimeout(() => {
        window.location.href = `${window.location.origin}/${companyId}/content-refresh?tab=past_reports`
      }, 2000)
    }
    return () => clearTimeout(timeout)
  }, [isSuccess])

  return (
    <Modal
      size="lg"
      visible={open}
      title="Change target keyword"
      subheader="Input the keyword you'd like to target for this page or select one from our recommendations"
      onCancel={() => setOpen(false)}
      okText="Regenerate Report"
      okButtonProps={{
        disabled: !keyword && !inputValue,
        loading: isGeneratingReports,
      }}
      onOk={handleRegenerateReport}
      destroyOnClose={true}
    >
      <div className={s.header}>
        <div className={s.title}>Currently targeted keyword</div>
        <div className={s.stats}>
          <div className={s.keyword}>{report?.target_keyword}</div>
          <div className={s.metrics}>
            <div>
              Your Position{' '}
              <span className={s.value}>
                {targetKeywordStats.position === 0
                  ? '>5'
                  : targetKeywordStats.position}
              </span>
            </div>
            <div>
              Vol{' '}
              <span className={s.value}>
                {new Intl.NumberFormat('en-US', {
                  notation: 'compact',
                  compactDisplay: 'short',
                }).format(targetKeywordStats.volume)}
              </span>
            </div>
            <div>
              Diff{' '}
              <span className={s.value}>{targetKeywordStats.difficulty}%</span>
            </div>
          </div>
        </div>
      </div>

      <div className={s.line} />

      <div className={s.optionsWrapper}>
        <Radio
          name="keyword"
          value={inputValue}
          className={s.inputOption}
          checked={!keyword}
          onChange={() => handleRadioChange()}
        >
          <div className={s.inputWrapper}>
            <p>Have a keyword in mind? Input it here</p>
            <Input
              placeholder="Enter a keyword"
              value={inputValue}
              onFocus={() => setKeyword(undefined)}
              onChange={e => {
                setInputValue(e.target.value)
              }}
            />
          </div>
        </Radio>

        {isLoading ? (
          <Spin />
        ) : (
          <div className={s.options}>
            <p className={s.optionTitle}>
              You can also click on any keyword that Pepper AI has suggested to
              target it
            </p>
            {keywords?.map(option => (
              <Radio
                name="keyword"
                className={s.option}
                value={keyword}
                key={option.keyword}
                checked={option.keyword === keyword}
                onChange={() => handleRadioChange(option.keyword)}
              >
                <div className={s.optionCard}>
                  <div>{option.keyword}</div>
                  <div className={s.metrics}>
                    <div>
                      Position{' '}
                      <span className={s.value}>
                        {option.position === 0 ? '>5' : option.position}
                      </span>
                    </div>
                    <div>
                      Vol{' '}
                      <span className={s.value}>
                        {new Intl.NumberFormat('en-US', {
                          notation: 'compact',
                          compactDisplay: 'short',
                        }).format(option.volume)}
                      </span>
                    </div>
                    <div>
                      Diff <span className={s.value}>{option.difficulty}%</span>
                    </div>
                  </div>
                </div>
              </Radio>
            ))}
          </div>
        )}
      </div>
    </Modal>
  )
}

export default ChangeKeywordModal
